import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import PageLoader from "components/pageLoader";
import ErrorCard from "components/errorCard";
import WeAreSorryCard from "components/weAreSorry";
import { API_FETCH_STATUS } from "constants/common";

import { FEEDBACK_PAYLOAD_MAP, VIEW_TYPE_MAP } from "./constants/feedback.constants";
import useFeedbackConfig from "components/feedback-framework/hooks/useFeedbackConfig";
import { faOnFeedbackPageLoad, faOnFeedbackSubmittedPopupLoad } from "./services/analytics.services";
import FeedbackStatus from "./feedback-status/feedback-status";
import FeedbackActions from "./feedback-actions/feedback-actions";
import useFeedbackQuestionsState from "components/feedback-framework/hooks/useFeedbackQuestionsState";

import FeedbackComponent from "./feedback-component/feedback-component";

import styles from "./feedback.component.scss";

const FeedbackContainer = ({ payload, viewType, onClose, showCloseBtn, onSubmit, ratingSelected, isAuth=false }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const uniqueId = query.get("uniqueId");

  const {
    feedbackConfig,
    feedbackConfigStatus,
    fetchFeedbackConfigData,
    validateFeedbackIdandFetchFeedbackConfig,
    saveFeedbackStatus,
    setSaveFeedbackStatus,
  } = useFeedbackConfig();
  const { setQuestions } = useFeedbackQuestionsState();
  const { requestType, lob, journey, customerInformation, requesterInformation } = payload;

  useEffect(() => {
    if (viewType === VIEW_TYPE_MAP.SMALL || viewType === VIEW_TYPE_MAP.MODAL) {
      fetchFeedbackConfigData(payload);
    } else {
      // TODO: need to check this for view type large
      faOnFeedbackPageLoad(uniqueId, isAuth);
      validateFeedbackIdandFetchFeedbackConfig(uniqueId);
    }
  }, []);

  useEffect(() => {
    setSaveFeedbackStatus("");
    return () => {
      setQuestions([]);
    };
  }, []);

  useEffect(() => {
    if(saveFeedbackStatus === API_FETCH_STATUS.SUCCESS){
      faOnFeedbackSubmittedPopupLoad({ isAuth });
    }
  },[saveFeedbackStatus])

  if (feedbackConfigStatus === API_FETCH_STATUS.LOADING) {
    return (
      <div className={styles.feedback_loader_container}>
        <PageLoader isCardView />
      </div>
    );
  }
  if (feedbackConfigStatus === API_FETCH_STATUS.ERROR) {
    return (
      <div className={styles.feedback_container}>
        <ErrorCard onRefresh={() => validateFeedbackIdandFetchFeedbackConfig(uniqueId)} />
      </div>
    );
  }
  if ((!requestType || !journey || !customerInformation || !requesterInformation) && !uniqueId) {
    return <WeAreSorryCard />;
  }

  const onCloseHandler = () => {
    setSaveFeedbackStatus("");
    setQuestions([]);
    onClose();
  };

  const { code = "", title = "", message = "" } = feedbackConfig || {};
  const isBusinessExceptionInFetchingFeedbackConfig = !!(code && message);

  if (isBusinessExceptionInFetchingFeedbackConfig || saveFeedbackStatus === API_FETCH_STATUS.SUCCESS) {
    return (
      <div className={viewType === VIEW_TYPE_MAP.MODAL ? styles.modal_feedback : styles.feedback_container}>
        <FeedbackStatus
          heading={isBusinessExceptionInFetchingFeedbackConfig ? title : "Feedback submitted"}
          description={
            isBusinessExceptionInFetchingFeedbackConfig ? message : "Thank you for successfully completing the feedback form."
          }
        />
        {showCloseBtn && <FeedbackActions onClose={onCloseHandler} />}
      </div>
    );
  }

  return (
    <div className={viewType === VIEW_TYPE_MAP.MODAL ? styles.modal_feedback : styles.feedback_container}>
      <FeedbackComponent viewType={viewType} onSubmit={onSubmit} ratingSelected={ratingSelected} isAuth={isAuth}/>
    </div>
  );
};

export default FeedbackContainer;

FeedbackContainer.defaultProps = {
  payload: FEEDBACK_PAYLOAD_MAP,
  viewType: VIEW_TYPE_MAP.SMALL,
  onClose: () => {},
  showCloseBtn: false,
  onSubmit: () => {},
};

FeedbackContainer.propTypes = {
  payload: PropTypes.object,
  viewType: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  showCloseBtn: PropTypes.bool,
  product: PropTypes.string,
  circle: PropTypes.string,
  segment: PropTypes.string,
  channel: PropTypes.string,
  channelId: PropTypes.string,
  journeyIdentifier: PropTypes.string,
};
